import { AnimatePresence, motion, useIsPresent, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, Info, RotateCcw, X } from "lucide-react";
import * as React from "react";
import { getCardImagePath, localCardImage, showPlaceholderOnError } from "@/lib/card-images";
import { categoryMeta, scoreComponentMeta } from "@/lib/mana";
import { describeReason, formatScore } from "@/lib/reasons";
import type {
  CardArt,
  CardScoreBreakdown,
  DeckCategoryId,
  DeckToken,
  RecommendationReason,
} from "@/lib/types";
import { cn, formatEur } from "@/lib/utils";
import { ManaCost, OracleText } from "./ManaSymbol";

export interface CardZoomProps {
  /** Viewport rect of the card at the moment it was opened. */
  origin: DOMRect;
  name: string;
  art: CardArt | null;
  glow: string;
  score?: CardScoreBreakdown;
  reasons?: readonly RecommendationReason[];
  primaryCategory?: DeckCategoryId;
  /** Offer the oracle text and score panel. */
  showDetails?: boolean;
  nameOf?: (oracleId: string) => string;
  /** Tokens created by the selected deck card. */
  relatedTokens?: readonly DeckToken[];
  /** Rendered when no local image exists. */
  fallback: React.ReactNode;
  /** Enlarged width in px; defaults to twice the origin width. */
  targetWidth?: number;
  onClose: () => void;
}

type Face = "front" | "back" | "details";

const MARGIN = 16;
const GAP = 10;
const RATIO = 88 / 63;
const ZOOM = 2;
const CAPTION_MIN_WIDTH = 300;

const FOCUSABLE = 'button, a[href], [tabindex]:not([tabindex="-1"])';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

/**
 * The card enlarged in place to about twice its size, clamped to the
 * viewport, with a compact caption below. Opens from and returns to the
 * card's own position using transforms only.
 */
export function CardZoom({
  origin,
  name,
  art,
  glow,
  score,
  reasons = [],
  primaryCategory,
  showDetails = true,
  nameOf = (id) => id,
  relatedTokens = [],
  fallback,
  targetWidth,
  onClose,
}: CardZoomProps) {
  const reduceMotion = useReducedMotion();
  const isPresent = useIsPresent();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLButtonElement>(null);
  const [face, setFace] = React.useState<Face>("front");
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const [tokenIndex, setTokenIndex] = React.useState(0);

  const front = localCardImage(art?.imageNormal ?? art?.imageLarge);
  const back = localCardImage(art?.backImageNormal);
  const faceImage = face === "back" ? back : front;
  const hasDetails = showDetails && art !== null;
  const hasReasons = reasons.length > 0;
  const hasTokens = relatedTokens.length > 0;

  // Measured once on open; scroll and resize close the zoom instead of re-laying it out.
  const [layout] = React.useState(() => {
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const captionHeight = hasReasons ? 104 : 58;
    const compactTokenRail = hasTokens && vw < 1100;
    const tokenRailHeight = compactTokenRail ? 200 : 0;
    const width = Math.max(
      0,
      Math.min(
        targetWidth ?? origin.width * ZOOM,
        vw - 2 * MARGIN,
        (vh - 2 * MARGIN - GAP - captionHeight - tokenRailHeight) / RATIO,
      ),
    );
    const height = width * RATIO;
    const centerX = origin.left + origin.width / 2;
    const centerY = origin.top + origin.height / 2;
    const left = clamp(centerX - width / 2, MARGIN, vw - MARGIN - width);
    const top = clamp(
      centerY - height / 2,
      MARGIN,
      vh - MARGIN - height - GAP - captionHeight - tokenRailHeight,
    );
    const captionWidth = Math.min(Math.max(width, CAPTION_MIN_WIDTH), vw - 2 * MARGIN);
    const captionLeft = clamp(
      left + width / 2 - captionWidth / 2,
      MARGIN,
      vw - MARGIN - captionWidth,
    );
    const visibleCount = Math.min(3, relatedTokens.length);
    let tokenRail: { left: number; top: number; width: number; cardWidth: number } | undefined;
    if (hasTokens) {
      const rightSpace = vw - MARGIN - (left + width) - GAP;
      const leftSpace = left - MARGIN - GAP;
      const side = rightSpace >= leftSpace ? "right" : "left";
      const sideSpace = Math.max(rightSpace, leftSpace);
      const sideCardWidth = Math.min(
        width,
        height / RATIO,
        (sideSpace - GAP * (visibleCount - 1)) / visibleCount,
      );
      const tokenCardWidth = compactTokenRail
        ? Math.min(112, (vw - 2 * MARGIN - GAP * (visibleCount - 1)) / visibleCount)
        : sideCardWidth;
      if (sideCardWidth >= 84 && !compactTokenRail) {
        const railWidth = visibleCount * tokenCardWidth + GAP * (visibleCount - 1);
        tokenRail = {
          left: side === "right" ? left + width + GAP : left - GAP - railWidth,
          top: top + (height - tokenCardWidth * RATIO) / 2,
          width: railWidth,
          cardWidth: tokenCardWidth,
        };
      } else {
        const railWidth = visibleCount * tokenCardWidth + GAP * (visibleCount - 1);
        tokenRail = {
          left: (vw - railWidth) / 2,
          top: top + height + GAP + captionHeight + GAP,
          width: railWidth,
          cardWidth: tokenCardWidth,
        };
      }
    }
    return {
      card: { left, top, width, height },
      caption: { left: captionLeft, top: top + height + GAP, width: captionWidth },
      captionHeight,
      tokenRail,
      from: {
        x: centerX - (left + width / 2),
        y: centerY - (top + height / 2),
        scale: width > 0 ? origin.width / width : 1,
      },
    };
  });

  const closedRef = React.useRef(false);
  const close = React.useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === "Tab" && rootRef.current) {
        const items = Array.from(rootRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) return;
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !rootRef.current.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !rootRef.current.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    // Any page scroll closes the zoom; scrolling inside it (details panel) does not.
    // Scroll events still queued from before opening (e.g. scroll-into-view) are ignored.
    const startX = window.scrollX;
    const startY = window.scrollY;
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      if (
        (target === document || target === document.documentElement) &&
        Math.abs(window.scrollX - startX) < 2 &&
        Math.abs(window.scrollY - startY) < 2
      ) {
        return;
      }
      close();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", close);
    };
  }, [close]);

  const from = reduceMotion ? { opacity: 0 } : { ...layout.from, opacity: 1 };
  const spring = { type: "spring" as const, stiffness: 320, damping: 30, mass: 0.8 };

  const faceLabel =
    face === "details" ? "card text and score" : face === "back" ? "back face" : "front face";

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${name}, enlarged`}
      className={cn("fixed inset-0 z-[70]", !isPresent && "pointer-events-none")}
    >
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={close}
      />

      <motion.button
        ref={cardRef}
        type="button"
        aria-label={`Close enlarged ${name} (showing ${faceLabel})`}
        onClick={close}
        className="perspective fixed block cursor-zoom-out rounded-[4.75%/3.4%] outline-none focus-visible:ring-2 focus-visible:ring-gold-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        style={{
          left: layout.card.left,
          top: layout.card.top,
          width: layout.card.width,
          height: layout.card.height,
          transformOrigin: "50% 50%",
        }}
        initial={from}
        animate={{ x: 0, y: 0, scale: 1, opacity: 1 }}
        exit={from}
        transition={reduceMotion ? { duration: 0.15 } : spring}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={face}
            className="absolute inset-0 overflow-hidden rounded-[4.75%/3.4%] bg-black"
            style={{ boxShadow: `0 0 44px 2px ${glow}88, 0 40px 70px -24px #000` }}
            initial={reduceMotion ? { opacity: 0 } : { rotateY: 90 }}
            animate={reduceMotion ? { opacity: 1 } : { rotateY: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { rotateY: -90 }}
            transition={{ duration: 0.16, ease: "easeInOut" }}
          >
            {face === "details" ? (
              <DetailsFace
                name={name}
                art={art}
                score={score}
                reasons={reasons}
                primaryCategory={primaryCategory}
                glow={glow}
                nameOf={nameOf}
              />
            ) : faceImage && faceImage !== failedSrc ? (
              <img
                src={faceImage}
                alt={face === "back" ? `${name} (back face)` : name}
                decoding="async"
                draggable={false}
                onError={() => setFailedSrc(faceImage)}
                className="h-full w-full object-cover"
              />
            ) : (
              fallback
            )}
          </motion.div>
        </AnimatePresence>
      </motion.button>

      <motion.div
        className="fixed rounded-xl border border-gold-500/25 bg-obsidian-950/85 px-3 py-2 text-xs shadow-xl backdrop-blur-md"
        style={{ left: layout.caption.left, top: layout.caption.top, width: layout.caption.width }}
        initial={{ opacity: 0, y: -6 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: { delay: reduceMotion ? 0 : 0.12, duration: 0.18 },
        }}
        exit={{ opacity: 0, transition: { duration: 0.1 } }}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-bold text-gold-100">{name}</p>
            <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
              {primaryCategory ? (
                <span style={{ color: categoryMeta[primaryCategory].color }}>
                  {categoryMeta[primaryCategory].icon} {categoryMeta[primaryCategory].label}
                </span>
              ) : (
                art?.setName && <span>{art.setName}</span>
              )}
              {art &&
                (art.priceEur === null ? (
                  <span title="No euro price for any paper printing">Price unknown</span>
                ) : (
                  <span>{formatEur(art.priceEur)}</span>
                ))}
              {score && (
                <span>
                  Score <span className="font-bold text-gold-200">{formatScore(score.total)}</span>
                </span>
              )}
            </p>
          </div>
          {hasDetails && (
            <ZoomAction
              label={face === "details" ? "Show card image" : "Show card text and score"}
              active={face === "details"}
              onClick={() => setFace((f) => (f === "details" ? "front" : "details"))}
            >
              <Info />
            </ZoomAction>
          )}
          {back && (
            <ZoomAction
              label={face === "back" ? "Show front face" : "Turn over to back face"}
              active={face === "back"}
              onClick={() => setFace((f) => (f === "back" ? "front" : "back"))}
            >
              <RotateCcw />
            </ZoomAction>
          )}
          {art?.scryfallUri && (
            <a
              href={art.scryfallUri}
              target="_blank"
              rel="noreferrer"
              title={`Open ${name} on Scryfall`}
              aria-label={`Open ${name} on Scryfall`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 text-gold-100 transition-colors hover:border-gold-300 [&_svg]:h-3.5 [&_svg]:w-3.5"
            >
              <ExternalLink />
            </a>
          )}
          <ZoomAction label="Close" onClick={close}>
            <X />
          </ZoomAction>
        </div>
        {hasReasons && (
          <ul className="mt-1.5 space-y-0.5 border-t border-white/10 pt-1.5 text-[11px] text-gold-50/80">
            {reasons.slice(0, 2).map((reason, index) => (
              <li key={index} className="flex gap-1.5">
                <span className="text-gold-400">✦</span>
                <span className="line-clamp-1">{describeReason(reason, nameOf)}</span>
              </li>
            ))}
          </ul>
        )}
      </motion.div>

      {layout.tokenRail && (
        <TokenSidecar
          cardName={name}
          tokens={relatedTokens}
          index={tokenIndex}
          onIndexChange={setTokenIndex}
          style={layout.tokenRail}
        />
      )}
    </div>
  );
}

function TokenSidecar({
  cardName,
  tokens,
  index,
  onIndexChange,
  style,
}: {
  cardName: string;
  tokens: readonly DeckToken[];
  index: number;
  onIndexChange: (index: number) => void;
  style: { left: number; top: number; width: number; cardWidth: number };
}) {
  const visibleCount = Math.min(3, tokens.length);
  const visible = tokens.slice(index, index + visibleCount);
  const lastIndex = Math.max(0, tokens.length - visibleCount);
  return (
    <motion.aside
      aria-label={`Tokens created by ${cardName}`}
      className="fixed z-[71]"
      style={{ left: style.left, top: style.top, width: style.width }}
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{ delay: 0.12, duration: 0.18 }}
    >
      <div className="mb-2 flex items-center justify-between rounded-lg border border-gold-500/25 bg-obsidian-950/85 px-2 py-1 text-[10px] uppercase tracking-wider text-gold-200 shadow-lg backdrop-blur-md">
        <span className="truncate">Tokens from {cardName}</span>
        {tokens.length > visibleCount && (
          <span className="ml-2 shrink-0 tabular-nums">
            {index + 1}–{Math.min(index + visibleCount, tokens.length)} / {tokens.length}
          </span>
        )}
      </div>
      <div
        className="relative grid gap-[10px]"
        style={{ gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))` }}
      >
        {visible.map((token) => (
          <div
            key={token.oracleId}
            className="min-w-0 overflow-hidden rounded-xl border border-gold-500/25 bg-obsidian-950/80 p-1.5 shadow-xl backdrop-blur-md"
          >
            <img
              src={getCardImagePath(token.scryfallId)}
              alt={token.name}
              draggable={false}
              onError={showPlaceholderOnError}
              className="aspect-[63/88] w-full rounded-lg object-cover"
            />
            <p className="mt-1 truncate text-center text-[10px] font-medium text-gold-100">
              {token.name}
            </p>
          </div>
        ))}
        {index > 0 && (
          <button
            type="button"
            aria-label="Previous tokens"
            onClick={() => onIndexChange(Math.max(0, index - 1))}
            className="absolute -left-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-gold-300/40 bg-black/90 text-gold-100 shadow-lg hover:bg-obsidian-800"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {index < lastIndex && (
          <button
            type="button"
            aria-label="Next tokens"
            onClick={() => onIndexChange(Math.min(lastIndex, index + 1))}
            className="absolute -right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-gold-300/40 bg-black/90 text-gold-100 shadow-lg hover:bg-obsidian-800"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </motion.aside>
  );
}

function ZoomAction({
  children,
  label,
  active = false,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5",
        active
          ? "border-gold-200 bg-gold-400 text-obsidian-950 shadow-glow"
          : "border-white/20 text-gold-100 hover:border-gold-300",
      )}
    >
      {children}
    </button>
  );
}

/** Oracle text, engine score breakdown and reasons, sized for the zoomed card. */
function DetailsFace({
  name,
  art,
  score,
  reasons,
  primaryCategory,
  glow,
  nameOf,
}: {
  name: string;
  art: CardArt | null;
  score?: CardScoreBreakdown;
  reasons: readonly RecommendationReason[];
  primaryCategory?: DeckCategoryId;
  glow: string;
  nameOf: (oracleId: string) => string;
}) {
  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden rounded-[4.75%/3.4%] border-2 border-gold-500/50 bg-gradient-to-b from-obsidian-900 via-obsidian-900 to-black p-4 text-left"
      style={{ boxShadow: `inset 0 0 30px -10px ${glow}88` }}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-lg font-bold leading-tight text-gold-100">{name}</h3>
        {art?.manaCost && <ManaCost cost={art.manaCost} symbolClassName="h-4 w-4" />}
      </div>
      <p className="mt-1 border-b border-gold-500/30 pb-1.5 text-sm italic text-gold-200/80">
        {art?.typeLine ?? "Unknown type"}
      </p>
      <div
        className="mt-3 flex-1 overflow-y-auto overscroll-contain pr-1 text-base leading-relaxed text-foreground/90"
        onClick={(event) => event.stopPropagation()}
      >
        {art?.oracleText ? (
          <OracleText text={art.oracleText} symbolClassName="h-4 w-4" />
        ) : (
          <p className="text-muted-foreground">No oracle text available.</p>
        )}
        {art?.power && art.toughness && (
          <p className="mt-2 text-right font-display font-bold text-gold-200">
            {art.power}/{art.toughness}
          </p>
        )}
        {art?.loyalty && (
          <p className="mt-2 text-right font-display font-bold text-gold-200">
            Loyalty {art.loyalty}
          </p>
        )}
        {score && score.components.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-white/10 pt-2">
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
              <span>Engine score</span>
              <span className="font-bold text-gold-200">{formatScore(score.total)}</span>
            </div>
            {score.components.map((component) => (
              <div key={component.id} className="flex items-center gap-2 text-sm">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: scoreComponentMeta[component.id].color }}
                />
                <span className="flex-1 text-foreground/80">
                  {scoreComponentMeta[component.id].label}
                </span>
                <span className={component.value < 0 ? "text-red-300" : "text-emerald-300"}>
                  {formatScore(component.value)}
                </span>
              </div>
            ))}
          </div>
        )}
        {reasons.length > 0 && (
          <ul className="mt-2 space-y-1 border-t border-white/10 pt-2 text-sm leading-relaxed text-gold-50/80">
            {reasons.slice(0, 6).map((reason, index) => (
              <li key={index} className="flex gap-1.5">
                <span className="text-gold-400">✦</span>
                {describeReason(reason, nameOf)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {primaryCategory && (
        <p
          className="mt-2 border-t border-white/10 pt-1.5 text-xs"
          style={{ color: categoryMeta[primaryCategory].color }}
        >
          {categoryMeta[primaryCategory].icon} {categoryMeta[primaryCategory].label}
        </p>
      )}
    </div>
  );
}
